import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { BookableServicesPanel } from './booking-services';
import { BookingLinksPanel } from './booking-links';
import { SurveysPanel } from '@/components/booking/SurveysPanel';
import { SetUpBookingWizard } from '@/components/booking/SetUpBookingWizard';

type BookingTab = 'services' | 'links' | 'surveys';
const TABS: BookingTab[] = ['services', 'links', 'surveys'];

export const Route = createFileRoute('/_dashboard/dashboard/settings/online-booking')({
  // `?tab=` makes the active tab addressable so redirected old links (and
  // bookmarks) land on the right tab.
  validateSearch: (search: Record<string, unknown>): { tab: BookingTab } => {
    const tab = search.tab;
    return {
      tab: TABS.includes(tab as BookingTab) ? (tab as BookingTab) : 'services',
    };
  },
  component: OnlineBookingPage,
});

/**
 * Unified Online Booking hub — one section for everything: a guided "set up a
 * booking" wizard (service → survey → link → share) plus management tabs for
 * services, links, and surveys. Replaces the separate Bookable Services /
 * Booking Links / Forms nav entries for the booking flow.
 */
function OnlineBookingPage() {
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [wizardOpen, setWizardOpen] = useState(false);

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Online Booking</h1>
          <p className="text-sm text-muted-foreground">
            Everything for self-serve booking — services, surveys, and shareable links — in one
            place.
          </p>
        </div>
        <Button onClick={() => setWizardOpen(true)}>
          <Sparkles className="mr-1.5 h-4 w-4" /> Set up a booking
        </Button>
      </header>

      <Tabs
        value={tab}
        onValueChange={(v) => void navigate({ search: { tab: v as BookingTab }, replace: true })}
      >
        <TabsList>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="links">Links</TabsTrigger>
          <TabsTrigger value="surveys">Surveys</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="mt-4">
          <BookableServicesPanel embedded />
        </TabsContent>
        <TabsContent value="links" className="mt-4">
          <BookingLinksPanel embedded />
        </TabsContent>
        <TabsContent value="surveys" className="mt-4">
          <SurveysPanel />
        </TabsContent>
      </Tabs>

      <SetUpBookingWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}
